import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionsListElement from '@/features/settings/components/permissions/PermissionsListElement';
import Calendar_04_Icon from '@/components/icons/Calendar_04_Icon';
import Analytics_01_Icon from '@/components/icons/Analytics_01_Icon';
import CalendarAdd_01_Icon from '@/components/icons/CalendarAdd_01_Icon';
import PackageAddIcon from '@/components/icons/PackageAddIcon';
import Calendar_02_Icon from '@/components/icons/Calendar_02_Icon';
import Calendar_03_Icon from '@/components/icons/Calendar_03_Icon';
import Login_01_Icon from '@/components/icons/Login_01_Icon';
import { useOwnerPermissionsQueryApi } from '@/features/owners/hooks/useOwnerPermissionsQueryApi.ts';
import { useOwnerPermissionsMutateApi } from '@/features/owners/hooks/useOwnerPermissionsMutateApi.ts';
import PermissionsListBookingLimitations from './PermissionsListBookingLimitations';
import { cn } from '@/lib/utils';

type Props = {
	ownerId: string;
	isMobile?: boolean;
};

const PermissionsList: FC<Props> = ({ ownerId, isMobile }) => {
	const { t } = useTranslation();
	const { permissions, isPermissionsLoading } =
		useOwnerPermissionsQueryApi(ownerId);
	const {
		doSetShowCalendar,
		isDoSetShowCalendarLoading,
		doSetShowFinances,
		isDoSetShowFinancesLoading,
		doSetCreateBookings,
		isDoSetCreateBookingsLoading,
		doSetPlaceOrders,
		isDoSetPlaceOrdersLoading,
		doSetLoginToAccount,
		isDoSetLoginToAccountLoading,
		doSetBookingDurationLimits,
		isDoSetBookingDurationLimitsLoading,
		doSetYearlyBookingLimit,
		isDoSetYearlyBookingLimitLoading,
	} = useOwnerPermissionsMutateApi();

	const isBusy =
		isPermissionsLoading ||
		isDoSetShowCalendarLoading ||
		isDoSetShowFinancesLoading ||
		isDoSetCreateBookingsLoading ||
		isDoSetPlaceOrdersLoading ||
		isDoSetLoginToAccountLoading ||
		isDoSetBookingDurationLimitsLoading ||
		isDoSetYearlyBookingLimitLoading;

	return (
		<div className='flex flex-col gap-4 select-none'>
			<div className='flex items-center justify-between gap-2'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t('main.settings.components.permissions.title')}
				</h3>
			</div>
			<div
				className={cn(
					'p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
					isMobile && 'p-0 border-0 bg-popover-30',
				)}>
				<div className='flex flex-col gap-2'>
					<h5 className='text-sm text-popover-800'>
						{t(
							'main.settings.components.permissions.list.general.title',
						)}
					</h5>
					<div className='flex flex-col gap-2'>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.general.elements.showCalendar',
							)}
							icon={Calendar_04_Icon}
							value={permissions?.showCalendar ?? false}
							onClick={(newValue) =>
								doSetShowCalendar({ ownerId, value: newValue })
							}
						/>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.general.elements.showFinances',
							)}
							icon={Analytics_01_Icon}
							value={permissions?.showFinances ?? false}
							onClick={(newValue) =>
								doSetShowFinances({ ownerId, value: newValue })
							}
						/>
					</div>
				</div>
				<div className='flex flex-col gap-2'>
					<h5 className='text-sm text-popover-800'>
						{t(
							'main.settings.components.permissions.list.actions.title',
						)}
					</h5>
					<div className='flex flex-col gap-2'>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.actions.elements.createBookings',
							)}
							icon={CalendarAdd_01_Icon}
							value={permissions?.createBookings ?? false}
							onClick={(newValue) =>
								doSetCreateBookings({
									ownerId,
									value: newValue,
								})
							}
						/>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.actions.elements.placeOrders',
							)}
							icon={PackageAddIcon}
							value={permissions?.placeOrders ?? false}
							onClick={(newValue) =>
								doSetPlaceOrders({ ownerId, value: newValue })
							}
						/>
					</div>
				</div>
				<div className='flex flex-col gap-2'>
					<h5 className='text-sm text-popover-800'>
						{t(
							'main.settings.components.permissions.list.limits.title',
						)}
					</h5>
					<div className='flex flex-col gap-2'>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.limits.elements.durationLimit',
							)}
							icon={Calendar_02_Icon}
							addition={
								<PermissionsListBookingLimitations
									ownerId={ownerId}
									initialDays={
										permissions?.bookingDurationLimit ?? 0
									}
									doFunc={doSetBookingDurationLimits}
								/>
							}
						/>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.limits.elements.yearlyLimit',
							)}
							icon={Calendar_03_Icon}
							addition={
								<PermissionsListBookingLimitations
									ownerId={ownerId}
									initialDays={
										permissions?.yearlyBookingLimit ?? 0
									}
									doFunc={doSetYearlyBookingLimit}
								/>
							}
						/>
					</div>
				</div>
				<div className='flex flex-col gap-2'>
					<h5 className='text-sm text-popover-800'>
						{t(
							'main.settings.components.permissions.list.account.title',
						)}
					</h5>
					<div className='flex flex-col gap-2'>
						<PermissionsListElement
							label={t(
								'main.settings.components.permissions.list.account.elements.loginToAccount',
							)}
							icon={Login_01_Icon}
							value={permissions?.loginToAccount ?? false}
							onClick={(newValue) =>
								doSetLoginToAccount({
									ownerId,
									value: newValue,
								})
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PermissionsList;
